import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { trpcClient } from "@tokenterminal/core/utils/trpc"
import { useMemo } from "react"

type ProjectData = Awaited<ReturnType<typeof trpcClient.projects.getProject.query>>

type MetricsBreakdownItem = {
  data_id: string
  chain_id?: string
  market_sector_id?: string
  breakdown_data_id?: string
  product_id?: string
  metrics: Record<
    string,
    {
      avg: number
      trend: number
      change: number
      sum?: number
      latest?: number
    }
  >
  sparklines?: Record<string, number[]>
}

const PROJECTS = [
  {
    id: "solana",
    label: "Solana",
    logo: "solana.png",
    metric2: "active-addresses-daily",
  },
  { id: "uniswap", label: "Uniswap", logo: "uniswap.png", metric2: "tvl" },
  { id: "aave", label: "Aave", logo: "aave.png", metric2: "active-loans" },
  {
    id: "hyperliquid",
    label: "Hyperliquid",
    logo: "hyperliquid.png",
    metric2: "transaction-count",
  },
] as const

const METRIC1_SLUG = "market-cap-circulating"

// Helper to convert slug to ID (hyphens to underscores)
function toId(slug: string): string {
  return slug.replace(/-/g, "_")
}

export function useProjectMetricsData(selectedProject: string) {
  // Fetch metrics configuration
  const { data: metricsConfigData } = useQuery({
    queryKey: ["metrics-configuration"],
    queryFn: async () => {
      return await trpcClient.metrics.getMetricsConfiguration.query()
    },
  })

  const metricsConfig = metricsConfigData?.data || []
  const metricsConfigById = toDictionary(metricsConfig, "metric_id")
  const metricsConfigBySlug = toDictionary(metricsConfig, "slug")

  // Fetch all project data in parallel using useQueries
  const projectQueries = useQueries({
    queries: PROJECTS.map((project) => ({
      queryKey: ["project", project.id],
      queryFn: async () => {
        return await trpcClient.projects.getProject.query({ slug: project.id })
      },
    })),
  })

  const projectDataMap = useMemo(() => {
    return new Map<string, ProjectData | undefined>(
      PROJECTS.map((project, index) => [project.id, projectQueries[index]?.data]),
    )
  }, [projectQueries])

  const selectedProjectData = projectDataMap.get(selectedProject)
  const selectedProjectConfig = PROJECTS.find((p) => p.id === selectedProject)

  // Fetch key metrics for selected project
  const { data: keyMetricsData } = useQuery({
    queryKey: ["key-metrics", selectedProject],
    queryFn: async () => {
      if (!selectedProjectData) return []
      const result = await trpcClient.projects.getKeyMetrics.query({
        project_slug: selectedProject,
      })
      return result || []
    },
    enabled: !!selectedProjectData,
    placeholderData: keepPreviousData,
  })

  const keyMetricIds = useMemo(() => {
    if (!keyMetricsData) return []
    return keyMetricsData.map((m: { metric_id: string }) => m.metric_id).slice(0, 5)
  }, [keyMetricsData])

  // Get chart metric IDs for combined breakdown query
  const chartMetricIds = useMemo(() => {
    if (!selectedProjectConfig || !metricsConfigBySlug.size) return []
    const metric1Id = toId(METRIC1_SLUG)
    const metric2Id = toId(selectedProjectConfig.metric2)
    return [metric1Id, metric2Id]
  }, [selectedProjectConfig, metricsConfigBySlug])

  // Combine all metric IDs for 365d breakdown (chart metrics + key metrics)
  const allMetricIds365d = useMemo(() => {
    return [...chartMetricIds, ...keyMetricIds]
  }, [chartMetricIds, keyMetricIds])

  // Fetch breakdown data in parallel: 24h, 30d for key metrics, and 365d for all metrics
  const breakdownQueries = useQueries({
    queries: [
      // 24h breakdown for key metrics
      {
        queryKey: ["breakdown", selectedProject, "24h", keyMetricIds],
        queryFn: async () => {
          if (!selectedProjectData || !keyMetricIds?.length) return {}
          const result = await trpcClient.metrics.postBreakdown.mutate({
            data_ids: [selectedProjectData.data_id],
            metric_ids: keyMetricIds,
            interval: "24h",
            groupBy: "projects",
            includeSelf: false,
          })
          const dataForProject = result.data.find(
            (item: MetricsBreakdownItem) => item.data_id === selectedProjectData.data_id,
          )
          return dataForProject?.metrics || {}
        },
        enabled: !!selectedProjectData && !!keyMetricIds?.length,
        placeholderData: keepPreviousData,
      },
      // 30d breakdown for key metrics
      {
        queryKey: ["breakdown", selectedProject, "30d", keyMetricIds],
        queryFn: async () => {
          if (!selectedProjectData || !keyMetricIds?.length) return {}
          const result = await trpcClient.metrics.postBreakdown.mutate({
            data_ids: [selectedProjectData.data_id],
            metric_ids: keyMetricIds,
            interval: "30d",
            groupBy: "projects",
            includeSelf: false,
          })
          const dataForProject = result.data.find(
            (item: MetricsBreakdownItem) => item.data_id === selectedProjectData.data_id,
          )
          return dataForProject?.metrics || {}
        },
        enabled: !!selectedProjectData && !!keyMetricIds?.length,
        placeholderData: keepPreviousData,
      },
      // 365d breakdown for chart metrics + key metrics (combined)
      {
        queryKey: ["breakdown", selectedProject, "365d", allMetricIds365d],
        queryFn: async () => {
          if (!selectedProjectData || !allMetricIds365d?.length) return {}
          const result = await trpcClient.metrics.postBreakdown.mutate({
            data_ids: [selectedProjectData.data_id],
            metric_ids: allMetricIds365d,
            interval: "365d",
            groupBy: "projects",
            includeSelf: false,
          })
          const dataForProject = result.data.find(
            (item: MetricsBreakdownItem) => item.data_id === selectedProjectData.data_id,
          )
          return dataForProject?.metrics || {}
        },
        enabled: !!selectedProjectData && !!allMetricIds365d?.length,
        placeholderData: keepPreviousData,
      },
    ],
  })

  const keyMetricBreakdown24h = useMemo(() => breakdownQueries[0]?.data || {}, [breakdownQueries])
  const keyMetricBreakdown30d = useMemo(() => breakdownQueries[1]?.data || {}, [breakdownQueries])
  const breakdown365d = useMemo(() => breakdownQueries[2]?.data || {}, [breakdownQueries])

  // Extract legend data from combined 365d breakdown
  const metric1LegendData = useMemo(() => {
    if (!breakdown365d || !selectedProjectData) return null
    return breakdown365d[toId(METRIC1_SLUG)] || null
  }, [breakdown365d, selectedProjectData])

  const metric2LegendData = useMemo(() => {
    if (!breakdown365d || !selectedProjectData || !selectedProjectConfig) return null
    return breakdown365d[toId(selectedProjectConfig.metric2)] || null
  }, [breakdown365d, selectedProjectData, selectedProjectConfig])

  const isLoading =
    projectQueries.some((query) => query.isLoading) ||
    !selectedProjectData ||
    !keyMetricIds ||
    breakdownQueries.some((query) => query.isLoading)

  return {
    metricsConfig,
    metricsConfigById,
    metricsConfigBySlug,
    projectDataMap,
    selectedProjectData,
    selectedProjectConfig,
    keyMetricsData,
    keyMetricIds,
    keyMetricBreakdown24h,
    keyMetricBreakdown30d,
    metric1LegendData,
    metric2LegendData,
    isLoading,
    PROJECTS,
    METRIC1_SLUG,
  }
}
